import axios, { AxiosInstance, InternalAxiosRequestConfig } from "axios";

/**
 * Adds X-Region header to axios request config
 * The region is read from localStorage (set by RegionContext)
 * Sends the country ID instead of the region code
 */
const addRegionHeader = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  // Get region from localStorage (set by RegionContext)
  // Handle both JSON format ({countryName: "ksa"}) and plain string format ("ksa")
  let countryId = null;
  try {
    const storedRegion = localStorage.getItem("region");
    if (storedRegion) {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(storedRegion);
        countryId = parsed?.countryId
      } catch {
        // If parsing fails, it's likely a plain string, use it directly
        countryId = null;
      }
    }
  } catch (error) {
    // If anything goes wrong, use default
    console.error("Error reading region from localStorage:", error);
  }

  // Add X-Region header to all requests with country ID?

  if (config.headers && countryId) {
    config.headers["X-Region"] = countryId;
  }

  return config;
};

/**
 * Creates an axios instance with X-Region header interceptor
 * The region is read from localStorage (set by RegionContext)
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Request interceptor to add X-Region header
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      return addRegionHeader(config);
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  return instance;
};

// Set up default axios interceptor so all axios requests (including direct imports) get X-Region header
axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return addRegionHeader(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Export the configured axios instance
export const apiClient = createAxiosInstance();

// Also export axios (now with interceptor configured)
export default apiClient;
